import { createContext, useContext, useEffect, useMemo, useState } from "react";

export interface CartProduct {
  id: number;
  slug: string;
  title: string;
  category: "print" | "original";
  image: string;
  price: number;
  currency: string;
  stockQuantity?: number;
}

export interface CartItem extends CartProduct {
  quantity: number;
}

interface CartContextValue {
  items: CartItem[];
  itemCount: number;
  subtotal: number;
  isOpen: boolean;
  addItem: (product: CartProduct) => void;
  removeItem: (id: number) => void;
  updateQuantity: (id: number, quantity: number) => void;
  clearCart: () => void;
  openCart: () => void;
  closeCart: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);
const STORAGE_KEY = "dalilaaint-cart";

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => localStorage.setItem(STORAGE_KEY, JSON.stringify(items)), [items]);

  const value = useMemo<CartContextValue>(() => ({
    items,
    itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
    subtotal: items.reduce((sum, item) => sum + item.price * item.quantity, 0),
    isOpen,
    addItem: (product) => {
      setItems((current) => {
        const existing = current.find((item) => item.id === product.id);
        if (!existing) return [...current, { ...product, quantity: 1 }];
        if (product.category === "original") return current;
        const max = product.stockQuantity ?? 99;
        return current.map((item) => item.id === product.id ? { ...item, quantity: Math.min(item.quantity + 1, max) } : item);
      });
      setIsOpen(true);
    },
    removeItem: (id) => setItems((current) => current.filter((item) => item.id !== id)),
    updateQuantity: (id, quantity) => setItems((current) => current.flatMap((item) => {
      if (item.id !== id) return [item];
      if (quantity <= 0) return [];
      const max = item.category === "original" ? 1 : item.stockQuantity ?? 99;
      return [{ ...item, quantity: Math.min(quantity, max) }];
    })),
    clearCart: () => setItems([]),
    openCart: () => setIsOpen(true),
    closeCart: () => setIsOpen(false),
  }), [items, isOpen]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const value = useContext(CartContext);
  if (!value) throw new Error("useCart must be used inside CartProvider");
  return value;
}
